/**
 * Géométrie du décrochage, côté régie.
 *
 * Miroir exact de `resolveLiveBreakBounds` (backend, `lib/live-break-plan.ts`) :
 * la boîte de décrochage calcule ses durées localement pour rester vivante
 * pendant que l'opérateur compose, et le serveur reste seul juge à la validation.
 * Les deux doivent donc dire la même chose, sans quoi l'opérateur compose un
 * décrochage que la validation refuse.
 *
 * La règle qui compte ici est celle du **dépassement** : un direct en fin
 * manuelle qui tourne au-delà de son heure de grille tient toujours l'antenne.
 * Sa fenêtre de décrochage court alors jusqu'à la prochaine ancre déjà posée —
 * celle qui le coupera si personne ne le fait — et non jusqu'à une heure de fin
 * périmée, qui laissait la boîte bloquée sur « Le bloc de direct est déjà
 * terminé » au moment précis où l'opérateur avait besoin de combler.
 */

/** Miroir de `LIVE_BREAK_OVERRUN_HORIZON_SEC` côté serveur. */
export const LIVE_BREAK_OVERRUN_HORIZON_SEC = 3600

export interface LiveBreakBoundsInput {
  /** Instant du décrochage, marge de préchargement comprise. */
  breakAtMs: number
  /** Heure de fin du bloc telle qu'elle est en grille. */
  scheduledEndMs: number
  /** Vrai quand le bloc est en fin manuelle. */
  manualEnd: boolean
  /** Début du premier élément déjà posé derrière, `null` si la grille est vide. */
  nextPendingStartMs: number | null
  /** Faux quand le direct ne reprend pas derrière le décrochage. */
  resume: boolean
}

export interface LiveBreakBounds {
  /** Fin retenue du bloc : c'est là que s'arrête la reprise du direct. */
  blockEndMs: number
  /** Fin de la fenêtre comblable. */
  windowEndMs: number
  /** Vrai quand le bloc tourne au-delà de son heure de grille. */
  overrun: boolean
}

export function resolveLiveBreakBounds(input: LiveBreakBoundsInput): LiveBreakBounds {
  const nextMs = input.nextPendingStartMs !== null && input.nextPendingStartMs > input.breakAtMs
    ? input.nextPendingStartMs
    : null

  const overrun = input.manualEnd && input.breakAtMs >= input.scheduledEndMs
  const blockEndMs = overrun
    ? nextMs ?? input.breakAtMs + LIVE_BREAK_OVERRUN_HORIZON_SEC * 1000
    : input.scheduledEndMs

  const windowEndMs = input.resume
    ? Math.min(blockEndMs, nextMs ?? blockEndMs)
    : nextMs ?? blockEndMs

  return { blockEndMs, windowEndMs, overrun }
}
